import type { DistributionPoint } from "../components/SalesProductAnalyticsTabsDashboard.data";

export const CHANNEL_OTHERS_THRESHOLD_PERCENT = 0;
export const CHANNEL_DISTRIBUTION_MAX_ROWS = 12;
export const CHANNEL_DISTRIBUTION_MAX_NAMED = 11;

/** UX palette: distinct bar colors by rank (Others uses last slot when present). */
export const CHANNEL_DISTRIBUTION_BAR_COLORS = [
  "#2f6bea",
  "#32cdbd",
  "#a855f7",
  "#60a5fa",
  "#10b981",
  "#f59e0b",
] as const;

export const channelDistributionBarColor = (index: number) =>
  CHANNEL_DISTRIBUTION_BAR_COLORS[index % CHANNEL_DISTRIBUTION_BAR_COLORS.length];

export type ChannelBarPoint = DistributionPoint & {
  percent: number;
  color: string;
  displayLabel: string;
  valueLabel: string;
  percentLabel: string;
  /** Sum of all channel values in the chart (for Others breakdown %). */
  totalSales: number;
  othersItems?: DistributionPoint[];
};

const isOthersChannelLabel = (label: string) => /^others$/i.test(label.trim());

export const formatCompactMetricValue = (value: number) => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })}M`;
  }
  if (abs >= 1_000) {
    return `${(value / 1_000).toLocaleString(undefined, { maximumFractionDigits: 2 })}K`;
  }
  return Math.round(value).toLocaleString();
};

export const formatChannelPercent = (value: number) =>
  `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

const formatValueWithPercent = (value: number, percent: number) =>
  `${formatCompactMetricValue(value)}\u00a0(${formatChannelPercent(percent)})`;

export const channelSharePercent = (value: number, total: number) =>
  total > 0 ? (value / total) * 100 : 0;

const roundedDisplaySharePercent = (value: number) => Math.round(value * 10) / 10;

const resolveChannelSharePercent = (item: DistributionPoint, total: number) =>
  Number.isFinite(item.sharePercent)
    ? Math.min(100, Math.max(0, item.sharePercent ?? 0))
    : channelSharePercent(item.value, total);

const sumChannelSharePercent = (items: DistributionPoint[], total: number, fallbackValue: number) => {
  const share = items.reduce((sum, item) => sum + resolveChannelSharePercent(item, total), 0);
  return share > 0 ? Math.min(100, share) : channelSharePercent(fallbackValue, total);
};

/**
 * Builds horizontal bar rows for Total Sales by Channels:
 * - descending by value
 * - at most 11 named channels displayed
 * - colors cycle through palette for 8th+ channels
 * - merges API-provided "Others" with binned small channels (no duplicate Others)
 */
export function buildChannelBarData(
  channels: DistributionPoint[],
  othersLabel: string = "Others"
): ChannelBarPoint[] {
  const total = channels.reduce((sum, item) => sum + item.value, 0);
  if (total <= 0) {
    return [];
  }

  const named: DistributionPoint[] = [];
  const preOthers: DistributionPoint[] = [];

  channels.forEach(item => {
    if (item.value <= 0) {
      return;
    }
    if (isOthersChannelLabel(item.label)) {
      preOthers.push(item);
    } else {
      named.push(item);
    }
  });

  const sortedNamed = [...named].sort((a, b) => b.value - a.value);
  const othersItems: DistributionPoint[] = [...preOthers];
  const eligible: DistributionPoint[] = [];

  sortedNamed.forEach(item => {
    const percent = resolveChannelSharePercent(item, total);
    if (roundedDisplaySharePercent(percent) <= CHANNEL_OTHERS_THRESHOLD_PERCENT) {
      othersItems.push(item);
    } else {
      eligible.push(item);
    }
  });

  const needsOthersRow = othersItems.length > 0;
  const maxNamed = needsOthersRow
    ? CHANNEL_DISTRIBUTION_MAX_NAMED
    : CHANNEL_DISTRIBUTION_MAX_ROWS;

  const topChannels = eligible.slice(0, maxNamed);
  eligible.slice(maxNamed).forEach(item => othersItems.push(item));

  const rows: ChannelBarPoint[] = topChannels.map((item, index) => {
    const percent = resolveChannelSharePercent(item, total);
    return {
      ...item,
      percent,
      totalSales: total,
      color: channelDistributionBarColor(index),
      displayLabel: formatValueWithPercent(item.value, percent),
      valueLabel: formatCompactMetricValue(item.value),
      percentLabel: `(${formatChannelPercent(percent)})`,
    };
  });

  const othersValue = othersItems.reduce((sum, item) => sum + item.value, 0);
  if (othersValue > 0) {
    const percent = sumChannelSharePercent(othersItems, total, othersValue);
    rows.push({
      label: othersLabel,
      value: othersValue,
      percent,
      totalSales: total,
      color: channelDistributionBarColor(rows.length),
      displayLabel: formatValueWithPercent(othersValue, percent),
      valueLabel: formatCompactMetricValue(othersValue),
      percentLabel: `(${formatChannelPercent(percent)})`,
      othersItems: [...othersItems].sort((a, b) => b.value - a.value),
    });
  }

  return rows
    .sort((a, b) => b.value - a.value)
    .map((row, index) => ({
      ...row,
      color: channelDistributionBarColor(index),
    }));
}
